import { z } from 'zod';
import { MoneySchema, SlugSchema, TimestampSchema } from './primitives.js';

/**
 * Sprint 100 — Daily payment reconciliation report.
 *
 * Stored at `platformBrands/{brandId}/reconciliationReports/{YYYY-MM-DD}`.
 * One doc per (brand, calendar-day in Asia/Kuala_Lumpur). Written by
 * the `reconcilePayments` scheduled function at 03:00 SGT the next day,
 * covering yesterday's payment activity.
 *
 * Purpose: give super-admin a single place to verify that every paid
 * payment intent landed a `collected` ledger entry in the right
 * tenant's wallet for the right amount. Discrepancies surface as
 * `intentLedgerMismatches[]` for manual investigation.
 *
 * What this report does NOT do (deferred):
 *   - Verify toyyibPay actually transferred money to Kilat's bank
 *     (requires toyyibPay settlement-statement upload or bank-statement
 *     ingestion — Sprint 101+).
 *   - Auto-correct mismatches. Super-admin reviews + writes manual
 *     `adjustment` ledger entries via the audit-logged callable.
 */

export const ReconciliationMismatchReasonSchema = z.enum([
  // Paid intent had no matching collected ledger entry (webhook bug or
  // tenant-side write failure).
  'paid_intent_no_ledger',
  // Ledger entry exists but the amount doesn't match the intent's amountSen.
  'amount_mismatch',
  // collected ledger entry exists but no corresponding paid intent
  // (forged webhook? manual super-admin entry?).
  'orphan_ledger',
  // Intent is paid but no subscription was issued for a wash_pack purchase.
  'wash_pack_not_issued',
  // Sprint 112 — paid wash_ticket intent but the ticket's `payment` field
  // was never set. Means `settleTicketFromIntent` (Sprint 110 webhook
  // handler) failed silently — operator's wallet got credited, but the
  // cashier still sees "unpaid" and might take cash → double-credit.
  'wash_ticket_not_settled',
]);
export type ReconciliationMismatchReason = z.infer<typeof ReconciliationMismatchReasonSchema>;

export const ReconciliationMismatchSchema = z.object({
  intentId: z.string().optional(),
  tenantId: z.string(),
  reason: ReconciliationMismatchReasonSchema,
  /** Free-form context — e.g., "intent.amountSen=2500 vs ledger.amount=2400". */
  detail: z.string().max(500),
  expectedSen: z.number().int().optional(),
  actualSen: z.number().int().optional(),
});
export type ReconciliationMismatch = z.infer<typeof ReconciliationMismatchSchema>;

export const ReconciliationByPurposeSchema = z.object({
  count: z.number().int().nonnegative(),
  totalSen: MoneySchema,
});
export type ReconciliationByPurpose = z.infer<typeof ReconciliationByPurposeSchema>;

export const ReconciliationReportSchema = z.object({
  id: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .describe('YYYY-MM-DD'),
  brandId: SlugSchema,
  /** Local-day start (00:00:00 in `Asia/Kuala_Lumpur`) the report covers. */
  periodStart: TimestampSchema,
  /** Local-day end (23:59:59.999 same timezone). */
  periodEnd: TimestampSchema,
  // ── Counts across the brand ────────────────────────────────────────
  paidIntentsCount: z.number().int().nonnegative(),
  failedIntentsCount: z.number().int().nonnegative(),
  /** Intents still in `created` status at periodEnd — likely stale; the
   *  expiry reaper job (Sprint 101) cleans these. */
  pendingIntentsCount: z.number().int().nonnegative(),
  // ── Money across the brand ─────────────────────────────────────────
  /** Sum of `amountSen` (operator's stated price) on paid intents. */
  totalAmountSen: MoneySchema,
  /** Sum of `pspFeeSen` (estimated, customer-paid) on paid intents. */
  totalEstimatedFeeSen: MoneySchema,
  /** Sum of `totalChargedSen` on paid intents (what customers actually paid). */
  totalChargedSen: MoneySchema,
  /** Sum of `collected` ledger entries on tenants' walletLedger that
   *  match paid intents in this window. Should equal `totalAmountSen`
   *  if every webhook fired cleanly. */
  totalCreditedSen: MoneySchema,
  // ── Per-purpose breakdown (for revenue analytics) ──────────────────
  byPurpose: z.object({
    wash_pack: ReconciliationByPurposeSchema,
    wash_ticket: ReconciliationByPurposeSchema,
  }),
  // ── Discrepancies ──────────────────────────────────────────────────
  /** Concrete problems super-admin should investigate. Empty = clean. */
  mismatches: z.array(ReconciliationMismatchSchema),
  /** Convenience: true when `mismatches.length === 0` AND
   *  `totalCreditedSen === totalAmountSen`. */
  clean: z.boolean(),
  // ── Metadata ───────────────────────────────────────────────────────
  computedAt: TimestampSchema,
  /** Set to 'system' for the scheduled cron, or super-admin uid when
   *  manually re-run from the dashboard. */
  computedBy: z.string(),
});
export type ReconciliationReport = z.infer<typeof ReconciliationReportSchema>;
